import React, {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {classNameScrollbar} from './tools/CommonHtml';
import {useColorModeTheme, UseAsComponent} from './tools/CommonReact';
import {LazyRoutes, useLevelNavigation} from './tools/LevelNavigation';
import {usePopPanel} from './tools/UIPopPanel';
import {hideTooltip, registerTooltipCtrl} from './tools/Tooltip';
import {useDialogs} from './tools/UIDialog';
import {appCtx} from './app/AppContext';
import {CheckAuth} from './app/Auth';
import {CheckLicense} from './app/UseLicense';
import {defaultPath, helpActions, menu, routes} from './routes';
import {ROUTES} from './routePaths.js';
import {v3Twins, V3FlagsProbe} from './v3Bridge';
import {SideBar} from './views/SideBar';
import {NavBar} from './views/NavBar';
import {ClientNoticeCIS, ClientNoticeEOL, ClientNoticePhp2Shell} from './views/ClientNotice';
import {ErrorBoundary} from './tools/errorBoundary/ErrorBoundary';
import {Toaster} from './v3/ui-kit';

// Module scope, not per render: useLevelNavigation mutates routes[path].instance, and a table rebuilt
// each render would reset it and flip the page's key, remounting it.
const appRoutes = {...routes, ...v3Twins};

export function App() {
	useColorModeTheme();
	const {panelCtrl, panelJsx} = usePopPanel();
	Object.assign(appCtx, {tooltip: panelCtrl});
	registerTooltipCtrl(panelCtrl);

	const {pathname, search} = useLocation();
    // Anchor unmounts before mouseleave can fire on nav; clear immediately. search covers same-path drill-downs (nav state is base64-encoded into the query).
	useEffect(() => { hideTooltip({now: true}); }, [pathname, search]);

	appCtx.nav = useLevelNavigation(appRoutes, defaultPath);
	if (appCtx.nav.redirected)
		return null;

	return (<>
		<CheckAuth>
			<CheckLicense>
				<V3FlagsProbe/>
				<div className='flex'>
					<SideBar {...{menu, helpActions}}/>
					<div data-test='page-background' className='grow flex flex-col bg-bg1 h-screen overflow-hidden relative'>
						<div className='flex flex-col gap-3 absolute bottom-4 z-10 right-4 w-1/3'>
							<ClientNoticePhp2Shell/>
							<ClientNoticeEOL/>
							<ClientNoticeCIS isOn={appCtx.nav.getCurPath().startsWith(ROUTES.CUSTOM_POLICY)}/>
						</div>
						<NavBar/>
						<ErrorBoundary>
							<div
								className={`p-8 grow h-full relative overflow-auto ${classNameScrollbar} outline-none`}
								tabIndex={0} ref={el => el && document.activeElement === document.body && el.focus()}>
								<LazyRoutes routes={appRoutes} {...{defaultPath}}/>
							</div>
						</ErrorBoundary>
					</div>
				</div>
			</CheckLicense>
		</CheckAuth>
		<UseAsComponent use={useDialogs} ctrl={appCtx} toJsx={ctrl => ctrl.dialogsJsx}/>
		{panelJsx}
		<Toaster/>
	</>);
}
